import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { getProjectUd1000ReportData } from "lib/api/getProjectUd1000ReportData";
import { getFileSortPref } from "lib/utils/fileSortpref";
import { useAppStore } from "lib/store/useAppStore";
import { buildUd1000PdfCoverPages, getProjectReportPages, ProjectReportPreviewPage } from "pages/projects/detail/components/report";

export default function ProjectReportIntroPreview({ projectId, project, listScope, onClose }) {
  const { t, i18n } = useTranslation();
  const userId = useAppStore(state => state.user?.id);
  const titleId = useId();
  const dialogRef = useRef(null);
  const viewportRef = useRef(null);
  const pageRef = useRef(null);
  const [data, setData] = useState({ loading: true, files: [], error: "" });
  const [retry, setRetry] = useState(0);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState({ scale: 1, height: 1120 });
  const scopeKey = JSON.stringify(listScope || {});
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, []);
  useEffect(() => {
    let current = true;
    setData({ loading: true, files: [], error: "" });
    getProjectUd1000ReportData(projectId, { ...getFileSortPref(userId), listScope: JSON.parse(scopeKey) }).then(result => {
      if (!current) return;
      setData({ loading: false, files: result.success ? result.files : [], error: result.success ? "" : result.message || "미리보기 정보를 불러오지 못했습니다." });
      setActive(0);
    }).catch(() => { if (current) setData({ loading: false, files: [], error: "미리보기 정보를 불러오지 못했습니다." }); });
    return () => { current = false; };
  }, [projectId, userId, scopeKey, retry]);
  const pages = useMemo(() => {
    const report = getProjectReportPages(project, project, data.files, t, "ud1000_fusion");
    return buildUd1000PdfCoverPages({ project, form: project, reportPages: report.pages.filter(page => !page.isEmpty && page.file), i18n });
  }, [project, data.files, t, i18n]);
  useEffect(() => {
    const viewport = viewportRef.current;
    const page = pageRef.current;
    if (!viewport || !page) return;
    const measure = () => {
      const pageHeight = page.scrollHeight;
      if (!viewport.clientWidth || !viewport.clientHeight || !pageHeight) return;
      const scale = Math.min(1, viewport.clientWidth / 860, viewport.clientHeight / pageHeight);
      setSize({ scale, height: pageHeight * scale });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(page);
    measure();
    return () => observer.disconnect();
  }, [active, data.loading, data.error]);
  return <dialog ref={dialogRef} className="projectIntroPreview" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <header className="projectIntroPreview__header">
      <div><h3 id={titleId}>{project.project_name}</h3></div>
      <button type="button" className="projectSettings__secondary" onClick={onClose} aria-label="미리보기 닫기"><X size={18} aria-hidden="true" /></button>
    </header>
    {data.loading ? <p className="projectIntroPreview__message" role="status">보고서 정보를 불러오는 중…</p> : data.error ?
      <div className="projectIntroPreview__message" role="alert"><p>{data.error}</p><button type="button" className="projectSettings__secondary" onClick={() => setRetry(value => value + 1)}>다시 불러오기</button></div> : <>
      <div className="projectIntroPreview__toolbar">
        <label>페이지 <select value={active} onChange={event => setActive(Number(event.target.value))}>
          {pages.map((page, index) => <option key={page.id} value={index}>{index + 1}. {page.title}</option>)}
        </select></label>
        <div><button type="button" disabled={active === 0} onClick={() => setActive(value => value - 1)} aria-label="이전 페이지"><ChevronLeft size={18} aria-hidden="true" /></button>
          <span aria-live="polite">{active + 1} / {pages.length}</span>
          <button type="button" disabled={active === pages.length - 1} onClick={() => setActive(value => value + 1)} aria-label="다음 페이지"><ChevronRight size={18} aria-hidden="true" /></button></div>
      </div>
      <div className="projectIntroPreview__body">
        <div ref={viewportRef} className="projectIntroPreview__viewport">
          <div style={{ height: size.height, width: 860 * size.scale, margin: "0 auto", overflow: "hidden" }}>
            <div ref={pageRef} className="projectIntroPreview__paper" style={{ transform: `scale(${size.scale})` }}>
              <ProjectReportPreviewPage page={pages[active]} t={t} i18n={i18n} selectedReportCategory="ud1000_fusion" />
            </div>
          </div>
        </div>
      </div>
    </>}
  </dialog>;
}
